import { normalizePublicRouteDataPath, } from "../../core/index.js";
import { compilePublicHead } from "../../seo/index.js";
import { isPublicHydrationState } from "../../hydration/index.js";
const PublicHeadSelector = "[data-public-head='true']";
const PublicHeadKeyAttribute = "data-public-head-key";
export function resolvePublicHydrationDecision(options) {
    const prerendered = options.rootElement?.dataset["prerendered"] === "true";
    const state = options.state;
    const stateCompatible = options.isCompatible(state);
    const publicState = isPublicHydrationState(state) ? state.publicSite : null;
    const renderedRouteUrl = typeof publicState?.routeData?.path === "string"
        ? publicState.routeData.path
        : undefined;
    const currentUrl = options.currentUrl === undefined
        ? undefined
        : normalizePublicRouteDataPath(options.currentUrl);
    const routeCompatible = currentUrl === undefined
        || (renderedRouteUrl !== undefined
            && normalizePublicRouteDataPath(renderedRouteUrl) === currentUrl);
    const compatible = stateCompatible && routeCompatible;
    if (prerendered && compatible)
        return {
            mode: "hydrate",
            state,
            reason: "compatible-prerender",
        };
    if (prerendered && !compatible) {
        const shellBuildId = options.rootElement?.dataset["publicBuildId"];
        const diagnostic = {
            code: "PUBLIC_HYDRATION_STATE_INCOMPATIBLE",
            message: routeCompatible
                ? "The prerendered public state is incompatible with the browser build. Falling back to CSR."
                : "The prerendered public route does not match the browser URL. Falling back to CSR.",
            state,
            ...(shellBuildId === undefined ? {} : { shellBuildId }),
            ...(publicState?.buildId === undefined ? {} : { stateBuildId: publicState.buildId }),
            ...(options.clientBuildId === undefined ? {} : { clientBuildId: options.clientBuildId }),
            ...(publicState === null ? {} : { stateProtocolVersion: publicState.protocolVersion }),
            ...(currentUrl === undefined ? {} : { currentUrl }),
            ...(renderedRouteUrl === undefined ? {} : { renderedRouteUrl }),
        };
        if (options.onIncompatibleState)
            options.onIncompatibleState(diagnostic);
        else
            console.warn(`[public-site] ${diagnostic.message}`, {
                code: diagnostic.code,
                shellBuildId: diagnostic.shellBuildId,
                stateBuildId: diagnostic.stateBuildId,
                clientBuildId: diagnostic.clientBuildId,
                stateProtocolVersion: diagnostic.stateProtocolVersion,
                currentUrl: diagnostic.currentUrl,
                renderedRouteUrl: diagnostic.renderedRouteUrl,
            });
        if (options.clearIncompatibleHtml !== false) {
            options.rootElement?.replaceChildren();
            if (options.rootElement)
                options.rootElement.dataset["prerendered"] = "false";
        }
    }
    return {
        mode: "csr",
        state: compatible ? state : undefined,
        reason: prerendered ? "incompatible-state" : "no-prerender",
    };
}
function getNodeTagName(node) {
    switch (node.kind) {
        case "title":
            return "title";
        case "meta":
            return "meta";
        case "link":
            return "link";
        case "structured-data":
            return "script";
    }
}
function createHeadElement(document, node) {
    return document.createElement(getNodeTagName(node));
}
function updateHeadElement(element, node) {
    element.dataset["publicHead"] = "true";
    element.setAttribute(PublicHeadKeyAttribute, node.key);
    switch (node.kind) {
        case "title":
            element.textContent = node.content;
            return;
        case "meta":
            element.removeAttribute("name");
            element.removeAttribute("property");
            if ("name" in node)
                element.setAttribute("name", node.name);
            else
                element.setAttribute("property", node.property);
            element.setAttribute("content", node.content);
            return;
        case "link":
            element.setAttribute("rel", node.rel);
            element.setAttribute("href", node.href);
            if (node.hreflang)
                element.setAttribute("hreflang", node.hreflang);
            else
                element.removeAttribute("hreflang");
            if (node.type)
                element.setAttribute("type", node.type);
            else
                element.removeAttribute("type");
            return;
        case "structured-data":
            element.setAttribute("type", node.type);
            element.textContent = JSON.stringify(node.json);
            return;
    }
}
function findInsertionAnchor(document, publicElements) {
    const lastPublicElement = publicElements.at(-1);
    if (lastPublicElement?.nextSibling)
        return lastPublicElement.nextSibling;
    return document.head.querySelector("link[rel='icon'], link[rel='apple-touch-icon'], link[rel='manifest'], link[rel='stylesheet'], script[src]");
}
export function removePublicHeadDescriptor(document) {
    for (const element of document.head.querySelectorAll(PublicHeadSelector))
        element.remove();
}
export function applyPublicHeadDescriptor(head, document = globalThis.document) {
    document.documentElement.lang = head.htmlLang;
    const nodes = compilePublicHead(head);
    const existingPublicElements = [
        ...document.head.querySelectorAll(PublicHeadSelector),
    ];
    const insertionAnchor = findInsertionAnchor(document, existingPublicElements);
    const elementByKey = new Map();
    for (const element of existingPublicElements) {
        const key = element.getAttribute(PublicHeadKeyAttribute);
        if (key && !elementByKey.has(key))
            elementByKey.set(key, element);
        else
            element.remove();
    }
    const unmanagedTitle = document.head.querySelector("title:not([data-public-head='true'])");
    if (unmanagedTitle && !elementByKey.has("title"))
        elementByKey.set("title", unmanagedTitle);
    const retained = new Set();
    for (const node of nodes) {
        const existing = elementByKey.get(node.key);
        const expectedTagName = getNodeTagName(node).toUpperCase();
        const element = existing?.tagName === expectedTagName
            ? existing
            : createHeadElement(document, node);
        if (existing && element !== existing)
            existing.remove();
        updateHeadElement(element, node);
        document.head.insertBefore(element, insertionAnchor);
        retained.add(element);
    }
    for (const element of elementByKey.values())
        if (!retained.has(element))
            element.remove();
}
//# sourceMappingURL=index.js.map